import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Select,
  MenuItem,
  Box,
  Divider,
  useTheme,
  ButtonGroup,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import Editor, { fontSizes, fontFamilies } from './rich-text-editor/Editor';
import { tokens } from '../../global/theme/tokens';
import ExoColorPicker from '../special/ExoColorPicker';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { TextBlockStyle } from '../editor/styling-controls/TextBlockStyle';
import { SignatureBlockStyle } from '../editor/styling-controls/SignatureBlockStyle';
import { Delta } from 'quill';
import { convertQuillHtmlToInlineAndClassHtml } from './rich-text-editor/convertQuillHtmlToInlineAndClassHtml.js';
import { useUserPreferences } from '../../global/useUserPreferences.jsx';
import { htmlToQuillDelta } from './rich-text-editor/quillHtmlToDelta.ts';

export const ExoRichTextEditor = forwardRef(
  (
    {
      debug,
      startHtml,
      onHtmlChange,
      hasError,
      textBlockType,
      allowSignature,
      signature,
      onSignatureChange,
      afterHtml,
      afterComponent,
      defaultColor,
      bgcolor,
      credentialsId,
      emailCredentials,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const cookies = new Cookies();
    const cookieFont = cookies.get('rtf-font');

    const { t } = useTranslation();

    const quillRef = useRef(null);
    const lastRangeRef = useRef(null);

    const [readOnly, setReadOnly] = useState(false);
    const [range, setRange] = useState(null);
    const [lastChange, setLastChange] = useState(null);
    const [selectedSignature, setSelectedSignature] = useState(null);
    // highlight buttons
    const [activeFormats, setActiveFormats] = useState({});

    useEffect(() => {
      setSelectedSignature(signature);
    }, [signature]);

    useEffect(() => {
      if (!quillRef?.current) return;
      if (!cookieFont) cookies.set('rtf-font', 'roboto');
      quillRef.current?.format('font', cookieFont || 'roboto');
      quillRef.current?.format('size', '12px');
      setActiveFormats({
        ...activeFormats,
        font: cookieFont || 'roboto',
        size: '12px',
      });
    }, [quillRef]);

    const handleSelectionChange = (range, oldRange, source) => {
      setRange(range);
      lastRangeRef.current = range;

      if (quillRef.current && range) {
        const formats = quillRef.current.getFormat(range);
        setActiveFormats(formats);
      }
    };

    function handleHtmlChange(e) {
      onHtmlChange(
        e +
          '<p style="padding-top: 12px;"></p>' +
          (selectedSignature?.text || ' ') +
          (afterHtml || ' ')
      );
    }

    // Funktion um Formatierung selektiv und fokussiert zu setzen
    const handlePreserveSelection = (callback) => (e) => {
      e.preventDefault(); // verhindert Verlust des Fokus
      const quill = quillRef.current;
      const range = lastRangeRef.current;

      setTimeout(() => {
        if (quill && range) {
          quill.setSelection(range);
        }
        callback(e); // Format anwenden
      }, 0);
    };

    // handle button press
    const formatText = (format) => {
      const quill = quillRef.current;
      setActiveFormats({ ...activeFormats, [format]: !activeFormats[format] });
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          const formats = quill.getFormat(range);
          quill.format(format, !formats[format]); // Toggle
        }
      }
    };

    // FONT-FAMILY Handler
    const handleFontChange = (e) => {
      const value = e.target.value || false;
      cookies.set('rtf-font', value);
      quillRef.current?.format('font', value);
      setActiveFormats({
        ...activeFormats,
        font: value,
      });
    };

    // FONT-SIZE Handler
    const handleSizeChange = (e) => {
      const value = e.target.value || false;
      quillRef.current?.format('size', value);
      setActiveFormats({
        ...activeFormats,
        size: value,
      });
    };

    // FONT-COLOR Handler
    const [openColor, setOpenColor] = useState(false);
    const handleColorChange = (value) => {
      quillRef.current?.format('color', value);
      setActiveFormats({
        ...activeFormats,
        color: value,
      });
    };

    const handleList = (type) => {
      quillRef.current?.format('list', type === activeFormats.list ? '' : type);
      setActiveFormats({
        ...activeFormats,
        list: type === activeFormats.list ? '' : type,
      });
    };

    // generate classes for fontsize
    function generateSxForFontSize() {
      var sx = {};
      fontSizes.forEach((size) => {
        sx[`.ql-size-${size}`] = { fontSize: size };
      });

      return sx;
    }

    // insert text
    function handleInsertDelta(delta) {
      const editor = quillRef.current;
      const range = editor?.getSelection();
      if (!editor || !range) return;

      // Insert delta at the current selection
      editor.updateContents(
        new Delta().retain(range.index).concat(delta),
        'user'
      );

      // Calculate the length of inserted content
      const insertedLength = delta.reduce((len, op) => {
        if (typeof op.insert === 'string') {
          return len + op.insert.length;
        } else {
          return len + 1; // for embeds like images
        }
      }, 0);

      // Move cursor to the end of inserted content
      editor.setSelection(range.index + insertedLength, 0, 'silent');
    }

    function handleInsertHtml(html) {
      const editor = quillRef.current;
      const range = editor?.getSelection();
      if (!editor || !range) return;

      const delta = htmlToQuillDelta(html);

      editor.updateContents(
        new Delta().retain(range.index).concat(delta),
        'user'
      );
    }

    useImperativeHandle(ref, () => ({
      insertHtml: handleInsertHtml,
      insertDelta: handleInsertDelta,
    }));

    return (
      <Box className="flex flex-col gap-2 w-full" sx={generateSxForFontSize()}>
        <Box
          className="rounded-lg flex gap-2 flex-wrap"
          sx={{
            'button.grouped': {
              bgcolor: colors.bgInput,
              border: `1px solid transparent`,
              minHeight: '37px',
              ':hover': {
                bgcolor: colors.card,
              },
            },
            '.active-style': {
              bgcolor: colors.info + '30!important',
              border: `1px solid ${colors.info}80!important`,
            },
          }}
        >
          <ButtonGroup variant="contained" size="small" disableElevation>
            <Button
              className={
                'grouped ' + (activeFormats.bold ? 'active-style' : '')
              }
              onClick={() => formatText('bold')}
            >
              <FormatBoldIcon />
            </Button>
            <Button
              className={
                'grouped ' + (activeFormats.italic ? 'active-style' : '')
              }
              onClick={() => formatText('italic')}
            >
              <FormatItalicIcon />
            </Button>
            <Button
              className={
                'grouped ' + (activeFormats.underline ? 'active-style' : '')
              }
              onClick={() => formatText('underline')}
            >
              <FormatUnderlinedIcon />
            </Button>
          </ButtonGroup>
          <Button
            onClick={() => {
              if (activeFormats.color && activeFormats.color !== defaultColor) {
                // reset color
                handleColorChange(defaultColor || null);
              } else {
                setOpenColor(true);
              }
            }}
            sx={{
              width: '37px',
              height: '37px',
              minWidth: 'unset',
              backgroundColor: activeFormats.color || colors.bgInput,
              ':hover': {
                bgcolor: activeFormats.color || colors.card,
              },
            }}
          >
            <FormatColorTextIcon />
          </Button>
          <ExoColorPicker
            open={openColor}
            onClose={() => setOpenColor(false)}
            color={activeFormats.color}
            onSelect={handleColorChange}
          />
          <Divider orientation="vertical" flexItem />
          <ButtonGroup variant="contained" size="small" disableElevation>
            {false && (
              <Button
                className={
                  'grouped ' +
                  (activeFormats.list === 'bullet' ? 'active-style' : '')
                }
                onClick={() => handleList('bullet')}
              >
                <FormatListBulletedIcon />
              </Button>
            )}
            <Button
              className={
                'grouped ' +
                (activeFormats.list === 'ordered' ? 'active-style' : '')
              }
              onClick={() => handleList('ordered')}
            >
              <FormatListNumberedIcon />
            </Button>
          </ButtonGroup>
          <Divider orientation="vertical" flexItem />
          {false && (
            <Select
              size="small"
              value={activeFormats.font || ''}
              displayEmpty
              onMouseDown={handlePreserveSelection(() => {})}
              onChange={handlePreserveSelection(handleFontChange)}
            >
              {fontFamilies.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          )}
          <Select
            size="small"
            value={activeFormats.size || ''}
            displayEmpty
            onMouseDown={handlePreserveSelection(() => {})}
            onChange={handlePreserveSelection(handleSizeChange)}
          >
            {fontSizes.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
          <Divider orientation="vertical" flexItem />
          {textBlockType && (
            <TextBlockStyle
              onInsert={handleInsertDelta}
              onChange={onHtmlChange}
              textBlockType={textBlockType}
            />
          )}
          {allowSignature && (
            <SignatureBlockStyle
              setSelectedSignature={(e) => {
                const quill = quillRef?.current;
                const quillHtml = quill.root.innerHTML;
                const inlineHtml =
                  convertQuillHtmlToInlineAndClassHtml(quillHtml);
                onHtmlChange(inlineHtml);
                setSelectedSignature(e);
                onSignatureChange(e);
              }}
              selectedSignature={selectedSignature}
              credentials={emailCredentials}
            />
          )}
        </Box>

        <Editor
          ref={quillRef}
          readOnly={readOnly}
          defaultValue={startHtml}
          onSelectionChange={handleSelectionChange}
          onTextChange={setLastChange}
          onHtmlChange={handleHtmlChange}
          hasError={hasError}
          afterHtml={selectedSignature?.text}
          afterComponent={afterComponent}
          defaultColor={defaultColor}
          bgcolor={bgcolor}
        />

        {debug && (
          <Box>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={readOnly}
                    onChange={(e) => setReadOnly(e.target.checked)}
                  />
                }
                label="Read Only"
              />

              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={() => {
                  alert(`Length: ${quillRef.current?.getLength()}`);
                }}
              >
                Get Content Length
              </Button>
            </Box>

            <Box mt={2}>
              <strong>Current Range:</strong>
              <br />
              {range ? JSON.stringify(range) : 'None'}
            </Box>

            <Box mt={1}>
              <strong>Current Styles:</strong>
              <br />
              {activeFormats ? JSON.stringify(activeFormats) : 'None'}
            </Box>

            <Box mt={1}>
              <strong>Last Change:</strong>
              <br />
              {lastChange ? JSON.stringify(lastChange.ops) : 'None'}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);
